import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { callable } from '../lib/callable';
import { LatestAlertsBanner } from '../components/LatestAlertsBanner';
import { MentorFilters } from '../components/MentorFilters';
import {
  parseFilters, serializeFilters, applyFilters, activeChips, hasActiveFilters,
  GENDERS, GENDER_LABEL,
  mentorOffersGames, mentorOffersHobbies, OFFERING_LABEL,
} from '../lib/mentorFilterState';
import { ALLOWED_EXAM_TAGS, examTagLabel, type ExamTag } from '../lib/examTags';
import { publicLabelFor, inferTrackFromContent, DEFAULT_TRACK } from '../lib/tracks';
import { resolveRail, type RailEntry } from '../lib/categoryRail';
import { BrandPill } from '../components/BrandPill';
import { type Brand } from '../lib/brand';
import { SEO_EXAMS } from '../lib/seoExams';
import { CITY_BY_SLUG } from '../lib/cities';
import { consultationLabel, consultationColors } from '../lib/consultation';
import { MentorAvatarImg } from '../components/MentorAvatarImg';
import { SuccessStoriesTicker } from '../components/SuccessStoriesTicker';
import { KunjeanHeroPanel } from '../components/KunjeanHeroPanel';
import { MentorsTicker } from '../components/MentorsTicker';
import { SITE_NAME } from '../lib/siteBrand';

// Live founder counter for the hero mentor CTA (non-blocking — shows the static label until
// getFoundersCount resolves, so it never delays first paint).
function HeroFounderCount() {
  const [n, setN] = useState<{ claimed: number; total: number } | null>(null);
  useEffect(() => {
    callable<unknown, { claimed: number; total: number }>('getFoundersCount', {})
      .then((r) => setN({ claimed: r.data.claimed, total: r.data.total })).catch(() => {});
  }, []);
  return <>{n ? `${n.claimed} / ${n.total} spots claimed` : '2,000 founder spots'}</>;
}

export interface RealMentor {
  mentorId: string;
  displayName: string;
  slug: string;
  tagline: string | null;
  profilePhotoUrl: string | null;
  examsTaught: string[];
  subjectsTaught: string[];
  hobbies: string[];
  sportsTaught: string[];
  qualifications: string[];
  yearsTeachingExperience: number;
  isFounder: boolean;
  founderNumber: number | null;
  category: 'academic' | 'skills' | 'hobbies' | 'spiritual' | null;
  offering: 'games' | 'hobbies' | null;
  gender: 'male' | 'female' | 'other' | null;
  brand: Brand | null;
  ratingAvg: number;
  ratingCount: number;
  state: string | null;
  city: string | null;
  teachingMode: string[];
  consultation: 'free' | 'paid' | 'both';
  demoVideoId: string | null;
}

function initials(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}



// A vertical filter group for the directory rail — options as a tidy list (label +
// count), highlighting the active one. When `collapsible`, only the top 6 show
// behind a "Show all" toggle so the long exam/subject lists don't make the rail
// taller than the card grid; the active option is always kept visible.
function RailList({ title, options, activeKey, onSelect, accent = '#1a4480', collapsible = false }: {
  title: string;
  options: { key: string; label: string; count?: number }[];
  activeKey: string; onSelect: (k: string) => void; accent?: string; collapsible?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const LIMIT = 6;
  let shown = collapsible && !expanded ? options.slice(0, LIMIT) : options;
  if (collapsible && !expanded && !shown.some((o) => o.key === activeKey)) {
    const active = options.find((o) => o.key === activeKey);
    if (active) shown = [...shown, active];
  }
  return (
    <div style={{ marginTop: '1.1rem' }}>
      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '0.35rem' }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.12rem' }}>
        {shown.map((o) => {
          const active = o.key === activeKey;
          return (
            <button key={o.key} type="button" onClick={() => onSelect(o.key)} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', width: '100%',
              padding: '0.34rem 0.55rem', borderRadius: 6, border: 'none', cursor: 'pointer', textAlign: 'left',
              background: active ? accent : 'transparent', color: active ? 'white' : '#334155',
              fontSize: '0.85rem', fontWeight: active ? 700 : 500,
            }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
              {typeof o.count === 'number' && <span style={{ fontSize: '0.72rem', opacity: active ? 0.9 : 0.85, flexShrink: 0 }}>{o.count}</span>}
            </button>
          );
        })}
      </div>
      {collapsible && options.length > LIMIT && (
        <button type="button" onClick={() => setExpanded((v) => !v)} style={{ marginTop: '0.3rem', background: 'none', border: 'none', padding: '0.2rem 0.55rem', color: accent, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>
          {expanded ? 'Show less' : `Show all ${options.length}`}
        </button>
      )}
    </div>
  );
}

function CategoryBadge({ category }: { category: 'academic' | 'skills' | 'hobbies' | 'spiritual' | null | undefined }) {
  if (!category) return null;
  const map = {
    academic:  { label: '🎓 Academics',      bg: '#dbeafe', fg: '#1e40af' },
    skills:    { label: '💼 Career Skills',  bg: '#dcfce7', fg: '#15803d' },
    // No combined badge: a hobbies-track mentor gets the specific ⚽ Games / 🎨 Hobbies pills
    // (see OfferingPill below), which are derived from what they actually teach.
    hobbies:   null,
    spiritual: { label: '🕉️ Spiritual',      bg: '#fef3c7', fg: '#92400e' },
  };
  const s = map[category];
  if (!s) return null;
  return (
    <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', background: s.bg, color: s.fg, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, marginLeft: '0.4rem', verticalAlign: 'middle' }}>{s.label}</span>
  );
}

// Content-derived Games / Hobbies badge, shown alongside the stored-category badge for a
// mentor who coaches games or teaches co-curriculars.
function OfferingPill({ label, bg, fg }: { label: string; bg: string; fg: string }) {
  return (
    <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', background: bg, color: fg, borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600, marginLeft: '0.4rem', verticalAlign: 'middle' }}>{label}</span>
  );
}



export function Home() {
  const [realMentors, setRealMentors] = useState<RealMentor[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Filter drawer for tablet/mobile; on desktop the rail is always visible (CSS).
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    callable<unknown, { mentors: RealMentor[] }>('listPublicMentors', {})
      .then((r) => setRealMentors(r.data.mentors)).catch(() => {});
  }, []);

  // Admin-configurable Category rail (label / order / visibility). Read via callable because
  // config/* is not client-readable. Null until loaded → resolveRail falls back to defaults.
  const [railConfig, setRailConfig] = useState<RailEntry[] | null>(null);
  useEffect(() => {
    callable<unknown, { entries: RailEntry[] }>('getCategoryRail', {})
      .then((r) => setRailConfig(r.data.entries)).catch(() => {});
  }, []);

  // Strategy-guide names for the home discovery strip (names only, links to /strategy-guides).
  const [guideNames, setGuideNames] = useState<{ id: string; title: string }[]>([]);
  useEffect(() => {
    callable<unknown, { guides: { id: string; title: string }[] }>('listStrategyGuides', {})
      .then((r) => setGuideNames((r.data.guides ?? []).map((g) => ({ id: g.id, title: g.title })))).catch(() => {});
  }, []);

  // Enhanced, URL-shareable filters drive the live-mentors directory (city, multi-exam,
  // mode, min experience/rating, qualifications). State lives in the query string.
  const filters = useMemo(() => parseFilters(searchParams), [searchParams]);
  const updateFilters = (next: typeof filters) => setSearchParams(serializeFilters(next), { replace: false });
  // Shuffle the mentor list once per load (Fisher–Yates) so no profile is
  // permanently pinned to the top — every mentor rotates through the prominent
  // grid spots. Re-runs only when the fetched list changes, not on filter edits,
  // so the order stays stable while a visitor is narrowing things down.
  const shuffledMentors = useMemo(() => {
    const arr = [...realMentors];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = arr[i]!;
      arr[i] = arr[j]!;
      arr[j] = tmp;
    }
    return arr;
  }, [realMentors]);
  const filteredRealMentors = useMemo(() => applyFilters(shuffledMentors, filters), [shuffledMentors, filters]);

  // Deep-link from the "find teachers" hashtags (/?exams=…#mentors): once mentors load,
  // scroll to the live-mentors grid. Native hash-scroll misses it (the section mounts
  // async after listPublicMentors resolves), so we do it once here.
  const scrolledToMentors = useRef(false);
  useEffect(() => {
    if (scrolledToMentors.current || realMentors.length === 0) return;
    if (typeof window === 'undefined' || window.location.hash !== '#mentors') return;
    const el = document.getElementById('mentors');
    if (el) { scrolledToMentors.current = true; requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' })); }
  }, [realMentors.length]);

  // ── Directory filter datasets — option lists + live counts for the rail. Same
  // data the old horizontal TabStrips used; only the presentation changes. ──
  // Count for a rail key: offerings ('off:*') are content-derived, the rest are stored category.
  const railCount = (key: string): number =>
    key === 'off:games' ? realMentors.filter(mentorOffersGames).length
      : key === 'off:hobbies' ? realMentors.filter(mentorOffersHobbies).length
        : realMentors.filter((m) => m.category === key).length;
  const categoryOptions = useMemo(() => [
    { key: 'all', label: 'All', count: realMentors.length },
    // Admin-configurable (label / order / visibility) via /admin/categories; falls back to
    // DEFAULT_RAIL when no config is set. Hidden entries are dropped.
    ...resolveRail(railConfig).filter((e) => !e.hidden).map((e) => ({ key: e.key, label: e.label, count: railCount(e.key) })),
  ], [realMentors, railConfig]);
  const examOptions = useMemo(() => {
    const opts = ALLOWED_EXAM_TAGS
      .map((t) => ({ key: t as string, label: examTagLabel(t), count: realMentors.filter((m) => (m.examsTaught ?? []).some((l) => String(l ?? '').trim().toLowerCase() === examTagLabel(t).trim().toLowerCase())).length }))
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count);
    return opts.length ? [{ key: 'all', label: 'All exams', count: realMentors.length }, ...opts] : [];
  }, [realMentors]);
  const subjectOptions = useMemo(() => {
    const counts = new Map<string, number>();
    realMentors.forEach((m) => (m.subjectsTaught ?? []).forEach((s) => counts.set(s, (counts.get(s) ?? 0) + 1)));
    const opts = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ key: label, label, count }));
    return opts.length ? [{ key: 'all', label: 'All subjects', count: realMentors.length }, ...opts] : [];
  }, [realMentors]);
  const hobbyOptions = useMemo(() => {
    const counts = new Map<string, number>();
    realMentors.forEach((m) => (m.hobbies ?? []).forEach((h) => counts.set(h, (counts.get(h) ?? 0) + 1)));
    const opts = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ key: label, label, count }));
    return opts.length ? [{ key: 'all', label: 'All hobbies', count: realMentors.length }, ...opts] : [];
  }, [realMentors]);
  const sportOptions = useMemo(() => {
    const counts = new Map<string, number>();
    realMentors.forEach((m) => (m.sportsTaught ?? []).forEach((s) => counts.set(s, (counts.get(s) ?? 0) + 1)));
    const opts = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ key: label, label, count }));
    return opts.length ? [{ key: 'all', label: 'All games', count: realMentors.length }, ...opts] : [];
  }, [realMentors]);
  const genderOptions = useMemo(() => {
    const opts = GENDERS.map((g) => ({ key: g as string, label: GENDER_LABEL[g], count: realMentors.filter((m) => m.gender === g).length })).filter((g) => g.count > 0);
    return opts.length ? [{ key: 'all', label: 'Any' }, ...opts] : [];
  }, [realMentors]);
  // City filter options from the REAL cities of live mentors (name-based, with counts) —
  // so the dropdown reflects where mentors actually are, not a fixed SEO-city list.
  const cityOptions = useMemo(() => {
    const counts = new Map<string, number>();
    realMentors.forEach((m) => { if (m.city) counts.set(m.city, (counts.get(m.city) ?? 0) + 1); });
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ value: name, label: name, count }));
  }, [realMentors]);
  const activeExam = filters.exams && filters.exams.length === 1 ? (filters.exams[0] as string) : 'all';
  const activeSubject = filters.subjects && filters.subjects.length === 1 ? filters.subjects[0]! : 'all';
  const activeHobby = filters.hobbies && filters.hobbies.length === 1 ? filters.hobbies[0]! : 'all';
  const activeSport = filters.sports && filters.sports.length === 1 ? filters.sports[0]! : 'all';

  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', minHeight: '100vh', padding: '3rem 1.5rem 5rem', background: 'linear-gradient(180deg, #fafbfc 0%, #f6f8fa 100%)' }}>
      {/* Mentor-signup badge — fixed, not in the auto-scrolling hero ticker. The
          ticker tile ("List Yourself Free") used to carry this CTA, but it
          auto-scrolls out of view every 32s regardless of whether the visitor
          scrolls the page, which is a bad place for the one conversion this
          whole site exists to drive (51/2,000 founder spots claimed at time
          of writing). This stays on screen the entire time instead. */}
      <a
        href="/mentor-signup"
        className="mentor-fixed-badge"
        style={{
          position: 'fixed', bottom: 20, right: 20, zIndex: 80,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem',
          padding: '0.6rem 1rem', borderRadius: 999,
          background: 'linear-gradient(135deg, #92400e 0%, #b45309 100%)',
          color: 'white', fontWeight: 800, fontSize: '0.9rem', textDecoration: 'none',
          boxShadow: '0 4px 16px rgba(217,119,6,0.45)',
          animation: 'mentorBadgePulse 2.2s ease-in-out infinite',
        }}
      >
        <style>{`
          @keyframes mentorBadgePulse {
            0%, 100% { box-shadow: 0 4px 16px rgba(217,119,6,0.45); }
            50% { box-shadow: 0 4px 22px rgba(245,158,11,0.85); }
          }
          /* Shrinks to an icon-only round tab on narrow screens so it never
             covers more than a thumb's width of content. The emoji becomes
             the icon; only the text label and subtitle hide. */
          @media (max-width: 480px) {
            .mentor-fixed-badge { padding: 0.65rem !important; border-radius: 50% !important; width: 52px; height: 52px; }
            .mentor-fixed-badge .mentor-badge-icon { font-size: 1.3rem; }
            .mentor-fixed-badge .mentor-badge-label,
            .mentor-fixed-badge .mentor-badge-sub { display: none; }
          }
        `}</style>
        <span className="mentor-badge-icon" aria-hidden="true">✨</span>
        <span className="mentor-badge-label">Become a Mentor</span>
        <span className="mentor-badge-sub" style={{ fontSize: '0.68rem', opacity: 0.92, fontWeight: 500 }}><HeroFounderCount /></span>
      </a>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        {/* Kunjean Heroes tribute panel: fixed 340×460 right column on desktop (identical
            constants on shreyaadi.co.in's own copy of this layout), stacked full-width
            below the hero on mobile — the panel's own @media query does the reflow. */}
        <div className="kj-hero-row" style={{ display: 'flex', gap: '1.5rem' }}>
          <style>{`
            .kj-hero-row { align-items: flex-start; }
            @media (max-width: 899px) { .kj-hero-row { flex-direction: column; align-items: stretch; } }
          `}</style>
          <div style={{ flex: 1, minWidth: 0 }}>
        <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
          {/* The unified brand crest — founder photo + SA octagon fused into one mark.
              Same asset as the header and social cards. */}
          <img
            src="/brand-crest.webp"
            alt="Shrey Aadi — founded by Col. Vishal Sharma (Retd.)"
            width={168}
            height={168}
            style={{ width: 'clamp(128px, 20vw, 176px)', height: 'auto', display: 'block', margin: '0 auto 0.9rem', filter: 'drop-shadow(0 4px 10px rgba(40,30,10,0.22))' }}
          />
          <h1 style={{ fontSize: 'clamp(1.85rem, 5vw, 3rem)', margin: 0, color: '#1a4480', letterSpacing: '-0.02em', lineHeight: 1.12 }}>
            Shrey Aadi Mentor Hub
          </h1>
          {/* Sub-line — forced onto ONE line (white-space: nowrap). The font scales
              with the viewport (vw) so the whole sentence shrinks to fit narrow
              screens instead of wrapping or overflowing the page. */}
          <p style={{ fontSize: 'clamp(0.95rem, 2.2vw, 1.3rem)', color: '#334155', fontWeight: 600, marginTop: '0.9rem', lineHeight: 1.45 }}>
            Where India&apos;s teachers &amp; mentors connect, teach &amp; build their brand — free.
          </p>
          {/* CTA tiles — auto-scrolling ticker (same convention as MentorsTicker /
              SuccessStoriesTicker: a doubled track translated -50%, paused on hover/focus so a
              moving target isn't a problem for mouse users, plain manual overflow-scroll under
              prefers-reduced-motion). The tile set is duplicated once for a seamless loop; the
              second copy is aria-hidden + not tab-focusable so screen readers and keyboard nav
              only see one set of links. Slow (32s) since these are primary action buttons, not
              passive content — a fast ticker would make "Find Your Mentor" annoying to catch. */}
          <div className="herocta" style={{ marginTop: '1.4rem', maxWidth: 1180, marginLeft: 'auto', marginRight: 'auto', textAlign: 'left' }}>
            <style>{`
              .herocta { overflow: hidden; }
              .herocta-track { display: flex; flex-wrap: nowrap; gap: 0.85rem; width: max-content; padding-bottom: 0.3rem; }
              @media (prefers-reduced-motion: no-preference) {
                .herocta-track { animation: heroCtaScroll 32s linear infinite; }
                .herocta:hover .herocta-track, .herocta:focus-within .herocta-track { animation-play-state: paused; }
              }
              @media (prefers-reduced-motion: reduce) {
                .herocta { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; -webkit-overflow-scrolling: touch; }
                .herocta::-webkit-scrollbar { display: none; }
              }
              @keyframes heroCtaScroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
              @keyframes examBuddyGlow { 0%,100% { box-shadow: 0 3px 12px rgba(27,67,50,0.28); } 50% { box-shadow: 0 3px 22px rgba(76,154,106,0.85); } }
              @keyframes examBuddyBadgePulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.75; transform: scale(1.1); } }
            `}</style>
            <div className="herocta-track">
              {[0, 1].map((copy) => (
                <div key={copy} aria-hidden={copy === 1 ? true : undefined} style={{ display: 'flex', flexWrap: 'nowrap', gap: '0.85rem' }}>
                  <a href="#find" tabIndex={copy === 1 ? -1 : undefined} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.85rem 1rem', flex: '1 0 auto', minWidth: 185, background: '#1a4480', color: 'white', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem', boxShadow: '0 3px 12px rgba(26,68,128,0.22)' }}>
                    🎓 Find Your Mentor
                    <span style={{ fontSize: '0.78rem', opacity: 0.92, fontWeight: 500 }}>For students &amp; parents · free</span>
                  </a>
                  {/* Exam Buddy — deliberately eye-catching (pulsing glow + a starred badge) so it's
                      noticed immediately on page load. Badge is a star, not "NEW" text — this is
                      flagged as important/priority, not a novelty callout, so the emphasis doesn't
                      read as temporary and fade once the tile stops being "new". Forest-green matches
                      Exam Buddy's own theme (lib/examBuddy/theme.ts) rather than reusing another
                      tile's color. Animation is a smooth pulse, not an on/off blink — real blinking
                      is a known accessibility problem (WCAG 2.2.2) and every other "attention"
                      pattern already on this site (LatestAlertsBanner, the "We're Hiring" pill) uses
                      the same smooth-pulse convention. */}
                  <a href="/exam-buddy" tabIndex={copy === 1 ? -1 : undefined} style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.85rem 1rem', flex: '1 0 auto', minWidth: 185, background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)', color: 'white', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem', animation: 'examBuddyGlow 1.8s ease-in-out infinite' }}>
                    <span aria-label="Important" style={{ position: 'absolute', top: -9, right: -9, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#dc2626', color: 'white', fontSize: '0.85rem', borderRadius: '50%', boxShadow: '0 2px 6px rgba(220,38,38,0.5)', animation: 'examBuddyBadgePulse 1.6s ease-in-out infinite' }}>★</span>
                    🎯 Exam Buddy
                    <span style={{ fontSize: '0.78rem', opacity: 0.95, fontWeight: 500 }}>Career quiz · stream picker · college finder</span>
                  </a>
                  <a href="/signup?mode=login" tabIndex={copy === 1 ? -1 : undefined} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.85rem 1rem', flex: '1 0 auto', minWidth: 185, background: 'linear-gradient(135deg, #334155 0%, #475569 100%)', color: 'white', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem', boxShadow: '0 3px 12px rgba(51,65,85,0.28)' }}>
                    🔑 Mentor Log in
                    <span style={{ fontSize: '0.78rem', opacity: 0.95, fontWeight: 500 }}>Returning mentors · your dashboard</span>
                  </a>
                  <a href="/strategy-guides" tabIndex={copy === 1 ? -1 : undefined} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.85rem 1rem', flex: '1 0 auto', minWidth: 185, background: 'linear-gradient(135deg, #115e59 0%, #0f766e 100%)', color: 'white', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem', boxShadow: '0 3px 12px rgba(15,118,110,0.28)' }}>
                    📘 Strategy Guides
                    <span style={{ fontSize: '0.78rem', opacity: 0.95, fontWeight: 500 }}>SSB · NDA · defence prep</span>
                  </a>
                  <a href="/success-stories" tabIndex={copy === 1 ? -1 : undefined} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.85rem 1rem', flex: '1 0 auto', minWidth: 185, background: 'linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)', color: 'white', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem', boxShadow: '0 3px 12px rgba(124,58,237,0.28)' }}>
                    🌟 Success Stories
                    <span style={{ fontSize: '0.78rem', opacity: 0.95, fontWeight: 500 }}>Ranks · admissions · results</span>
                  </a>
                  <a href="/question-papers" tabIndex={copy === 1 ? -1 : undefined} style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.85rem 1rem', flex: '1 0 auto', minWidth: 185, background: 'linear-gradient(135deg, #991b1b 0%, #b91c1c 100%)', color: 'white', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '1.1rem', boxShadow: '0 3px 12px rgba(185,28,28,0.28)' }}>
                    📄 Question Papers
                    <span style={{ fontSize: '0.78rem', opacity: 0.95, fontWeight: 500 }}>Previous years · PYQs · answer keys</span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </header>
          </div>
          <KunjeanHeroPanel />
        </div>

        <LatestAlertsBanner />

        {guideNames.length > 0 && (
          <section style={{ padding: '1.25rem 1.5rem', background: 'white', border: '1px solid #ddd', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem' }}>
              <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.1rem' }}>📘 Strategy Guides</h2>
              <a href="/strategy-guides" style={{ color: '#1a4480', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none' }}>See all →</a>
            </div>
            {/* Names only — auto-scrolling ticker, same convention as MentorsTicker /
                SuccessStoriesTicker (doubled track translated -50%, paused on hover/focus,
                plain manual overflow-scroll under prefers-reduced-motion). Safe to auto-scroll
                here (unlike the hero CTA row) since every pill links to the same
                /strategy-guides destination — there's no "wrong pill scrolled away" problem. */}
            <div className="mtabs">
              <style>{`
                .mtabs { overflow: hidden; padding: 0.7rem 0 0.2rem; }
                .mtabs-track { display: flex; gap: 0.5rem; width: max-content; }
                @media (prefers-reduced-motion: no-preference) {
                  .mtabs-track { animation: mtabs-scroll ${Math.max(20, guideNames.length * 3)}s linear infinite; }
                  .mtabs:hover .mtabs-track, .mtabs:focus-within .mtabs-track { animation-play-state: paused; }
                }
                @media (prefers-reduced-motion: reduce) {
                  .mtabs { overflow-x: auto; scrollbar-width: none; -ms-overflow-style: none; -webkit-overflow-scrolling: touch; }
                  .mtabs::-webkit-scrollbar { display: none; }
                }
                @keyframes mtabs-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
              `}</style>
              <div className="mtabs-track">
                {[...guideNames, ...guideNames].map((g, i) => (
                  <a key={`${g.id}-${i}`} href="/strategy-guides" title={g.title} aria-hidden={i >= guideNames.length ? true : undefined} tabIndex={i >= guideNames.length ? -1 : undefined} style={{ flex: '0 0 auto', whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', padding: '0.45rem 0.95rem', borderRadius: 999, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#1a4480', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>📘 {g.title}</a>
                ))}
              </div>
            </div>
          </section>
        )}

        <MentorsTicker mentors={realMentors} />
        <SuccessStoriesTicker />

        {realMentors.length > 0 && (
          <section id="mentors" style={{ scrollMarginTop: '80px', padding: '1.5rem', background: 'white', border: '1px solid #ddd', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.5rem' }}>
            <style>{`
              .dir-grid { display: grid; grid-template-columns: 260px 1fr; gap: 1.25rem; align-items: start; margin-top: 1rem; }
              .dir-rail { position: sticky; top: 80px; align-self: start; }
              .dir-railbox { border: 1px solid #e5e7eb; border-radius: 10px; background: #fff; padding: 0.9rem 0.85rem; max-height: calc(100vh - 96px); overflow-y: auto; }
              .dir-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 1rem; }
              .dir-filter-toggle { display: none; }
              @media (max-width: 1023px) {
                .dir-grid { grid-template-columns: 1fr; }
                .dir-rail { position: static; display: none; }
                .dir-rail.open { display: block; }
                .dir-filter-toggle { display: inline-flex; align-items: center; gap: 0.4rem; }
                .dir-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
              }
              @media (max-width: 639px) {
                .dir-cards { grid-template-columns: 1fr; justify-items: center; }
                /* Below 640px the vertical-stack card (photo-top, everything centered below)
                   put only ~1 mentor per screen. Switch to a compact horizontal row — photo
                   left, details right, whole row tappable — so 4-5 mentors fit per screen,
                   matching how any mobile directory (Airbnb listings, Justdial) reads. Width
                   matches the MentorsTicker/SuccessStoriesTicker card width (min(300px,82vw))
                   so all three mentor-card surfaces read as one consistent width on mobile. */
                .dir-card { flex-direction: row !important; align-items: center !important; text-align: left !important; padding: 0.65rem 0.85rem !important; width: min(300px, 82vw) !important; }
                .dir-card-avatar img, .dir-card-avatar > div > div { width: 56px !important; height: 56px !important; font-size: 1.1rem !important; }
                .dir-card-desktop-only { display: none !important; }
                .dir-card-mobile-only { display: block !important; }
              }
              .dir-card-mobile-only { display: none; }
            `}</style>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.2rem' }}>Live mentors</h2>
              <button type="button" className="dir-filter-toggle" onClick={() => setShowFilters((v) => !v)} style={{ padding: '0.4rem 0.9rem', borderRadius: 8, border: '1px solid #1a4480', background: showFilters ? '#1a4480' : 'white', color: showFilters ? 'white' : '#1a4480', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>☰ Filters</button>
            </div>
            <p style={{ color: '#6b7280', fontSize: '1rem', marginTop: '0.5rem' }}>Verified mentors taking learners now. Click any card for the full profile.</p>

            <div className="dir-grid">
              {/* ── Filter rail — sticky on desktop, a toggled drawer on ≤1023px ── */}
              <aside className={`dir-rail${showFilters ? ' open' : ''}`}>
                <div className="dir-railbox">
                  <input
                    type="search"
                    value={filters.q ?? ''}
                    onChange={(e) => setSearchParams(serializeFilters({ ...filters, q: e.target.value || undefined }), { replace: true })}
                    placeholder="🔍 Search name / subject / exam / city"
                    style={{ width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.7rem', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '1rem' }}
                  />

                  <RailList title="Category" options={categoryOptions} activeKey={filters.offering ? `off:${filters.offering}` : (filters.category ?? 'all')} onSelect={(k) => {
                    if (k === 'off:games') updateFilters({ ...filters, category: undefined, offering: 'games' });
                    else if (k === 'off:hobbies') updateFilters({ ...filters, category: undefined, offering: 'hobbies' });
                    else updateFilters({ ...filters, offering: undefined, category: k === 'all' ? undefined : (k as typeof filters.category) });
                  }} />

                  {examOptions.length > 0 && (
                    <RailList title="Exams" accent="#0f766e" collapsible options={examOptions} activeKey={activeExam} onSelect={(k) => updateFilters({ ...filters, exams: k === 'all' ? undefined : [k as ExamTag] })} />
                  )}

                  {subjectOptions.length > 0 && (
                    <RailList title="Subjects" accent="#b45309" collapsible options={subjectOptions} activeKey={activeSubject} onSelect={(k) => updateFilters({ ...filters, subjects: k === 'all' ? undefined : [k] })} />
                  )}

                  {hobbyOptions.length > 0 && (
                    <RailList title="Hobbies" accent="#7c3aed" collapsible options={hobbyOptions} activeKey={activeHobby} onSelect={(k) => updateFilters({ ...filters, hobbies: k === 'all' ? undefined : [k] })} />
                  )}

                  {sportOptions.length > 0 && (
                    <RailList title="Games" accent="#0f766e" collapsible options={sportOptions} activeKey={activeSport} onSelect={(k) => updateFilters({ ...filters, sports: k === 'all' ? undefined : [k] })} />
                  )}

                  {genderOptions.length > 0 && (
                    <div style={{ marginTop: '1.1rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', marginBottom: '0.4rem' }}>Gender</div>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {genderOptions.map((g) => {
                          const active = (filters.gender ?? 'all') === g.key;
                          return (
                            <button key={g.key} type="button" onClick={() => updateFilters({ ...filters, gender: g.key === 'all' ? undefined : (g.key as typeof filters.gender) })} style={{
                              padding: '0.3rem 0.75rem', borderRadius: 999, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                              border: active ? '1px solid #6d28d9' : '1px solid #d1d5db', background: active ? '#6d28d9' : 'white', color: active ? 'white' : '#334155',
                            }}>{g.label}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: '1.1rem' }}>
                    <MentorFilters state={filters} onChange={updateFilters} cityOptions={cityOptions} hobbyOptions={hobbyOptions} sportOptions={sportOptions} categoryOptions={categoryOptions} />
                  </div>

                  {hasActiveFilters(filters) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center', marginTop: '1.1rem', paddingTop: '0.85rem', borderTop: '1px solid #eef2f7' }}>
                      {activeChips(filters).map((c) => (
                        <button key={c.key} type="button" onClick={() => updateFilters(c.next)} style={{ padding: '0.25rem 0.6rem', borderRadius: 999, border: '1px solid #1a4480', background: '#eef2ff', color: '#1a4480', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>{c.label} ✕</button>
                      ))}
                      <button type="button" onClick={() => setSearchParams({})} style={{ padding: '0.25rem 0.6rem', borderRadius: 999, border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>Clear all</button>
                    </div>
                  )}
                </div>
              </aside>

              {/* ── Card grid ── */}
              <div className="dir-main">
                <p style={{ color: '#475569', fontSize: '0.875rem', margin: '0 0 0.85rem' }}>Showing {filteredRealMentors.length} mentor{filteredRealMentors.length === 1 ? '' : 's'}{hasActiveFilters(filters) ? ' matching your filters' : ''}.</p>
                {filteredRealMentors.length > 0 ? (
                <div className="dir-cards">
                  {filteredRealMentors.map((m) => (
                    <a key={m.mentorId} className="dir-card" href={'/m/' + m.slug} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '1.1rem 1rem', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fafbfc', textDecoration: 'none', color: 'inherit' }}>
                      <div className="dir-card-avatar" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div>
                          {m.brand && <div className="dir-card-desktop-only" style={{ width: '75%', margin: '0 auto 0.6rem' }}><BrandPill brand={m.brand} wide label="" /></div>}
                          {m.profilePhotoUrl ? (
                            <MentorAvatarImg src={m.profilePhotoUrl} alt={m.displayName} px={84} style={{ width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', background: '#eee' }} />
                          ) : (
                            <div style={{ width: 84, height: 84, borderRadius: '50%', background: '#1a4480', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '1.5rem' }}>{initials(m.displayName)}</div>
                          )}
                        </div>
                        {m.demoVideoId && (
                          <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', padding: '0.35rem 0.5rem', borderRadius: 8, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#1a4480' }}>
                            <span style={{ width: 22, height: 22, borderRadius: '50%', background: '#1a4480', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>▶</span>
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.1, whiteSpace: 'nowrap' }}>Demo Video</span>
                          </span>
                        )}
                      </div>
                      <div style={{ minWidth: 0, width: '100%' }}>
                      <div style={{ fontWeight: 700, color: '#222', marginTop: '0.6rem', fontSize: '1rem' }}>{m.displayName}</div>
                      <div className="dir-card-desktop-only" style={{ marginTop: '0.3rem', display: 'flex', gap: '0.3rem', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
                        <CategoryBadge category={m.category ?? inferTrackFromContent(m)} />
                        {mentorOffersGames(m) && <OfferingPill label={OFFERING_LABEL.games} bg="#dcfce7" fg="#166534" />}
                        {mentorOffersHobbies(m) && <OfferingPill label={OFFERING_LABEL.hobbies} bg="#f3e8ff" fg="#7c3aed" />}
                      </div>
                      {m.ratingCount > 0 && (
                        <div className="dir-card-desktop-only" style={{ fontSize: '0.875rem', marginTop: '0.35rem' }}>
                          <span style={{ color: '#d97706' }}>{'★'.repeat(Math.round(m.ratingAvg))}<span style={{ color: '#9ca3af' }}>{'★'.repeat(5 - Math.round(m.ratingAvg))}</span></span>
                          <span style={{ color: '#444', fontWeight: 600, marginLeft: '0.3rem' }}>{m.ratingAvg.toFixed(1)}</span>
                          <span style={{ color: '#6b7280', marginLeft: '0.15rem' }}>({m.ratingCount})</span>
                        </div>
                      )}
                      <div className="dir-card-desktop-only" style={{ marginTop: '0.45rem', fontSize: '0.875rem', color: '#475569', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {/* Label follows the mentor's track. An un-categorised mentor's track is
                            inferred from what they actually teach, so legacy records aren't
                            labelled with a bucket nobody chose. */}
                        <strong style={{ color: '#334155' }}>{publicLabelFor(m.category ?? inferTrackFromContent(m) ?? DEFAULT_TRACK, 'examsTaught')}:</strong> {m.examsTaught.slice(0, 3).join(', ')}{m.examsTaught.length > 3 ? '…' : ''}
                      </div>
                      {m.sportsTaught && m.sportsTaught.length > 0 && (
                        <div className="dir-card-desktop-only" style={{ marginTop: '0.2rem', fontSize: '0.875rem', color: '#0f766e', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          ⚽ {m.sportsTaught.slice(0, 3).join(', ')}{m.sportsTaught.length > 3 ? '…' : ''}
                        </div>
                      )}
                      {m.hobbies && m.hobbies.length > 0 && (
                        <div className="dir-card-desktop-only" style={{ marginTop: '0.2rem', fontSize: '0.875rem', color: '#6d28d9', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          🎨 {m.hobbies.slice(0, 3).join(', ')}{m.hobbies.length > 3 ? '…' : ''}
                        </div>
                      )}
                      <div className="dir-card-desktop-only" style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.15rem' }}>{m.yearsTeachingExperience} yrs{m.city ? ' · 📍 ' + m.city : ''}</div>
                      <div className="dir-card-desktop-only" style={{ marginTop: '0.7rem', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: consultationColors(m.consultation).fg, background: consultationColors(m.consultation).bg, borderRadius: 999, padding: '0.14rem 0.5rem' }}>{consultationLabel(m.consultation)}</span>
                        <span style={{ fontSize: '0.875rem', color: '#1a4480', fontWeight: 700 }}>View →</span>
                      </div>
                      {/* Mobile-only compact line — replaces the 4 rows above (badges/rating/
                          sports/hobbies/years-city) with one scannable line so a row stays
                          ≤ ~90px tall and 4-5 mentors fit per screen. */}
                      <div className="dir-card-mobile-only" style={{ marginTop: '0.2rem', fontSize: '0.875rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {m.examsTaught.slice(0, 2).join(', ')}
                        {m.ratingCount > 0 ? ` · ★${m.ratingAvg.toFixed(1)} (${m.ratingCount})` : ''}
                        {` · ${m.yearsTeachingExperience} yrs`}
                        {m.city ? ` · 📍 ${m.city}` : ''}
                      </div>
                      </div>
                    </a>
                  ))}
                </div>
                ) : (
                  <div style={{ padding: '1.5rem', textAlign: 'center', background: '#fafbfc', border: '1px dashed #cbd5e1', borderRadius: 8 }}>
                    <p style={{ margin: 0, color: '#475569', fontWeight: 600 }}>No mentors match your filters yet.</p>
                    <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => setSearchParams({})} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #1a4480', background: '#1a4480', color: 'white', fontWeight: 600, cursor: 'pointer' }}>Clear all filters</button>
                      {(() => {
                        const city = filters.city ? CITY_BY_SLUG[filters.city] : undefined;
                        const exam = filters.exams && filters.exams.length ? SEO_EXAMS.find((e) => e.examTags.includes(filters.exams![0]!)) : undefined;
                        return city && exam ? (
                          <a href={`/m/${exam.urlSlug}-mentor-in-${city.slug}`} style={{ padding: '0.5rem 1rem', borderRadius: 8, border: '1px solid #1a4480', background: 'white', color: '#1a4480', fontWeight: 600, textDecoration: 'none' }}>Get notified when {exam.displayName} mentors join in {city.displayName} →</a>
                        ) : null;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {realMentors.length < 20 && (
        <section id="find" style={{ scrollMarginTop: '80px', padding: '1.75rem', background: 'white', border: '1px solid #ddd', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', textAlign: 'center' }}>
          <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.2rem' }}>More verified mentors are joining</h2>
          <p style={{ color: '#475569', fontSize: '1rem', marginTop: '0.6rem', lineHeight: 1.55, maxWidth: 580, marginLeft: 'auto', marginRight: 'auto' }}>
            We&apos;re onboarding verified mentors across India for every major exam. Be the first to know when one joins for your exam.
          </p>
          <div style={{ marginTop: '1.2rem', display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="/updates" style={{ padding: '0.7rem 1.4rem', background: '#1a4480', color: 'white', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem' }}>📢 Get exam updates</a>
            <a href="/mentor-signup" style={{ padding: '0.7rem 1.4rem', background: 'white', color: '#1a4480', border: '2px solid #1a4480', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: '0.95rem' }}>✨ Are you a mentor? List free</a>
          </div>
          <p style={{ marginTop: '1.25rem', fontSize: '0.875rem', color: '#6b7280' }}>
            36 states &amp; UTs · 400+ districts · sub-district level coverage for major metros.
          </p>
        </section>
        )}

        <section style={{ marginTop: '2rem', padding: '1.75rem', background: 'white', border: '1px solid #ddd', borderRadius: '12px' }}>
          <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.2rem' }}>Common questions</h2>
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            {[
              { q: `Is ${SITE_NAME} free for parents and students?`, a: 'Yes, completely free. Mentors pay a small flat listing fee to be on the platform — never a commission on what they earn. You pay tuition directly to the mentor, at rates you agree with them.' },
              { q: 'Are these mentors verified?', a: 'Every active mentor confirms their identity (mobile and email) and we review their qualifications before they appear in the directory. Our first 2,000 are Founder Members.' },
              { q: 'What exams and subjects are covered?', a: 'JEE Main, JEE Advanced, NEET UG/PG, CUET, NDA, CDS, AFCAT, Agniveer, UPSC, SSC, banking, CAT, GATE, all board exams, plus spiritual sciences — astrology, numerology, vastu, palmistry. Our Updates feed also tracks defence & paramilitary recruitment (Army, Navy, Air Force, Coast Guard, CRPF, CISF, BSF, ITBP) and central/state government job notices.' },
              { q: 'How does the inquiry work?', a: 'Submit a quick form with your name, contact, and what you need. Your inquiry goes directly to the mentor. They reply within 24–48 hours via your preferred channel (email, WhatsApp, or call).' },
              { q: 'How is my data protected?', a: 'We comply with the DPDP Act 2023. Your contact details are shared only with the mentor you choose. We never sell data or send promotional spam.' },
            ].map((f, i) => (
              <details key={i} style={{ background: '#fafbfc', padding: '0.85rem 1.1rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#1a4480', fontSize: '0.98rem' }}>{f.q}</summary>
                <p style={{ marginTop: '0.6rem', marginBottom: 0, color: '#444', lineHeight: 1.55, fontSize: '1rem' }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section style={{ marginTop: '2rem', padding: '1.75rem', background: 'linear-gradient(135deg, #1a4480 0%, #1d4ed8 100%)', borderRadius: '12px', color: 'white', textAlign: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '1.4rem' }}>Are you a mentor?</h2>
          <p style={{ marginTop: '0.75rem', fontSize: '1rem', opacity: 0.95, lineHeight: 1.5, maxWidth: 580, marginLeft: 'auto', marginRight: 'auto' }}>
            Join our directory. Reach parents and students looking for serious mentors in your city. Free to list during launch.
          </p>
          <a href="/signup" style={{ display: 'inline-block', marginTop: '1.25rem', padding: '0.75rem 1.75rem', background: 'white', color: '#1a4480', textDecoration: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '1rem' }}>List as a Mentor →</a>
          <div style={{ marginTop: '1rem', fontSize: '0.9rem', opacity: 0.9 }}>
            Already a mentor? <a href="/signup?mode=login" style={{ color: 'white', textDecoration: 'underline', fontWeight: 600 }}>Log in →</a>
          </div>
        </section>

        <section style={{ marginTop: '2rem', padding: '1.5rem', background: 'white', border: '1px solid #ddd', borderRadius: '12px' }}>
          <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.15rem' }}>📘 Free Career Handbook</h2>
          <p style={{ color: '#555', marginTop: '0.5rem', fontSize: '1rem', lineHeight: 1.5 }}>
            While we build, here&apos;s our free 65-page guide for Indian students &amp; parents:
            choosing a stream, every major entrance exam, 50+ career profiles with realistic 2026 salary bands, and 60+ FAQs.
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <a href="/career-handbook.pdf" target="_blank" rel="noreferrer" style={{ padding: '0.6rem 1.25rem', background: 'white', color: '#1a4480', border: '1px solid #1a4480', textDecoration: 'none', borderRadius: '6px', fontWeight: 500, fontSize: '0.95rem' }}>📖 Read online</a>
          </div>
          <p style={{ marginTop: '0.75rem', color: '#6b7280', fontSize: '0.875rem' }}>Read online only · 2026 Edition</p>
        </section>

        <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '2.5rem', textAlign: 'center' }}>
          Questions or interested in being an early mentor?{' '}
          <a href="mailto:hello@shreyaadieducation.in" style={{ color: '#1a4480' }}>hello@shreyaadieducation.in</a>
        </p>
      </div>

    </main>
  );
}

